import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/interfaces/stores/RootState';
import { HealthCheck } from '@/interfaces/models/HealthCheck';
import HealthCheckApiService from '@/api/http/HealthCheckApiService';
import Filter from '@/interfaces/api/Filter';
import { Pagination } from '@/interfaces/api/Pagination';
import { Page } from '@/interfaces/api/Page';
import { AxiosResponse } from 'axios';

interface HealthCheckState {
  items: HealthCheck[];
  active: HealthCheck | null;
  filter: Filter;
  pagination: {
    total: number;
  };
}

const api: HealthCheckApiService = new HealthCheckApiService();

const state: HealthCheckState = {
  items: [],
  active: null,
  filter: {},
  pagination: {
    total: 10,
  },
};

const actions: ActionTree<HealthCheckState, RootState> = {
  fetch({ commit, state }, data: { page: Page }) {
    return api
      .getHealthChecks(data.page, state.filter)
      .then((res: AxiosResponse<HealthCheck[] | Pagination<HealthCheck>>) => {
        commit('setItems', res.data);
      });
  },
  store({ commit }, data: Partial<HealthCheck>) {
    return api.store(data).then((res: AxiosResponse<HealthCheck>) => {
      commit('addItem', res.data);
    });
  },
  show({ commit }, data: { id: string }) {
    return api.show(data).then((res: AxiosResponse<HealthCheck>) => {
      commit('setActive', res.data);
    });
  },
  update({ commit }, data: Partial<HealthCheck>) {
    return api.update(data).then((res: AxiosResponse<HealthCheck>) => {
      commit('update', res.data);
    });
  },
  delete({ commit }, data: { id: string }) {
    return api.softDelete(data).then(() => {
      commit('delete', { id: data.id });
    });
  },
  setFilter({ commit }, filter: Filter) {
    commit('setFilter', filter);
  },
};

const mutations: MutationTree<HealthCheckState> = {
  setItems(state: HealthCheckState, data: Pagination<HealthCheck>) {
    state.items = (data.data as HealthCheck[]) || data;
    state.pagination.total = data?.total || 0;
  },
  setActive(state: HealthCheckState, data: HealthCheck) {
    state.active = data;
  },
  addItem(state: HealthCheckState, data: HealthCheck) {
    state.items.push(data);
  },
  update(state: HealthCheckState, data: HealthCheck) {
    state.items = [...state.items.filter((r: HealthCheck) => r._id !== data._id), data];
  },
  delete(state: HealthCheckState, data: { id: string }) {
    state.items = [...state.items.filter((r: HealthCheck) => r._id !== data.id)];
  },
  setFilter(state: HealthCheckState, data: Filter) {
    state.filter = data;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
